<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Create New Business</h5>
          <p class="page-sub">Set up a new business profile</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="allErrors.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in allErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <form @submit.prevent="createCompany">
        <div class="form-card">
  
          <!-- Business Info -->
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="building" />
            </div>
            <div>
              <p class="section-title">Business Information</p>
              <p class="section-sub">Enter the business name and contact details</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group fields-col-full">
              <label class="field-label">Business Name <span class="req">*</span></label>
              <input v-model="name" @input="clearError('name')" type="text" :class="['field-input', validationErrors.name ? 'field-input--error' : '']" placeholder="Enter business name" required/>
              <span class="field-error" v-if="validationErrors.name">{{ validationErrors.name[0] }}</span>
            </div>

            <div class="field-group">
              <label class="field-label">Email Address</label>
              <input v-model="email" @input="clearError('email')" type="email" :class="['field-input', validationErrors.email ? 'field-input--error' : '']" placeholder="primary@business.com" />
              <span class="field-error" v-if="validationErrors.email">{{ validationErrors.email[0] }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Email Address 2 <span class="optional">optional</span></label>
              <input v-model="email2" @input="clearError('email2')" type="email" :class="['field-input', validationErrors.email2 ? 'field-input--error' : '']" placeholder="secondary@business.com" />
              <span class="field-error" v-if="validationErrors.email2">{{ validationErrors.email2[0] }}</span>
            </div>

            <div class="field-group">
              <label class="field-label">Phone Number</label>
              <input v-model="phone_number" @input="clearError('phone_number')" @keypress="filterPhone" type="tel" :class="['field-input', validationErrors.phone_number ? 'field-input--error' : '']" placeholder="e.g. 08012345678" inputmode="tel" />
              <span class="field-error" v-if="validationErrors.phone_number">{{ validationErrors.phone_number[0] }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Phone Number 2 <span class="optional">optional</span></label>
              <input v-model="phone_number2" @input="clearError('phone_number2')" @keypress="filterPhone" type="tel" :class="['field-input', validationErrors.phone_number2 ? 'field-input--error' : '']" placeholder="e.g. 08012345678" inputmode="tel" />
              <span class="field-error" v-if="validationErrors.phone_number2">{{ validationErrors.phone_number2[0] }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Phone Number 3 <span class="optional">optional</span></label>
              <input v-model="phone_number3" @input="clearError('phone_number3')" @keypress="filterPhone" type="tel" :class="['field-input', validationErrors.phone_number3 ? 'field-input--error' : '']" placeholder="e.g. 08012345678" inputmode="tel" />
              <span class="field-error" v-if="validationErrors.phone_number3">{{ validationErrors.phone_number3[0] }}</span>
            </div>
            
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Location -->
          <div class="section-header">
            <div class="section-icon section-icon--orange">
              <font-awesome-icon icon="map-marker-alt" />
            </div>
            <div>
              <p class="section-title">Location</p>
              <p class="section-sub">Set country, state, city and address</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Country Code <span class="req">*</span></label>
              <select v-model="country_code" type="text" class="field-input field-select" required>
                <option value="NG">NG</option>
                <option value="GB">GB</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Country</label>
              <select v-model="country_id" class="field-input field-select" @change="getStates($event)">
                <option value="">Choose country</option>
                <option :value="c.id" v-for="c in countries" :key="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">State</label>
              <select v-model="state_id" class="field-input field-select" @change="getCities($event)" :disabled="!states.length">
                <option value="">Choose state</option>
                <option :value="s.id" v-for="s in states" :key="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">City</label>
              <select v-model="city_id" class="field-input field-select" :disabled="!cities.length">
                <option value="">Choose city</option>
                <option :value="c.id" v-for="c in cities" :key="c.id">{{ c.name }}</option>
              </select>
            </div>
  
            <div class="field-group fields-col-full">
              <label class="field-label">Business Address <span class="req">*</span></label>
              <textarea v-model="address" class="field-input field-textarea" placeholder="Enter full business address" rows="3"></textarea>
              <span class="field-error" v-if="validationErrors.address">{{ validationErrors.address[0] }}</span>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Logo Upload -->
          <div class="section-header">
            <div class="section-icon section-icon--purple">
              <font-awesome-icon icon="image" />
            </div>
            <div>
              <p class="section-title">Business Logo</p>
              <p class="section-sub">Upload a logo to represent your business</p>
            </div>
          </div>
  
          <label class="logo-upload" :class="logo ? 'logo-upload--filled' : ''">
            <input type="file" accept="image/png,image/jpeg,image/jpg" class="logo-file-input" @change="onFileSelected($event)" />
            <div v-if="logo" class="logo-preview">
              <img :src="logo" alt="Business logo" />
              <div class="logo-overlay">
                <font-awesome-icon icon="camera" />
                <span>Change logo</span>
              </div>
            </div>
            <div v-else class="logo-placeholder">
              <div class="logo-placeholder-icon">
                <font-awesome-icon icon="camera" />
              </div>
              <p class="logo-placeholder-text">Click to upload logo</p>
              <p class="logo-placeholder-hint">PNG or JPG, max 5 MB</p>
            </div>
          </label>
          <span class="field-error mt-2 d-block" v-if="validationErrors.logo">{{ validationErrors.logo[0] }}</span>
  
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary-action" :disabled="crudLoading">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ crudLoading ? 'Creating...' : 'Create Business' }}
          </button>
        </div>
  
      </form>
    </div>
  </template>
  
  <script>
  import { ref, computed } from "@vue/reactivity";
  import AdminHeader from "./administrationheadertab.vue";
  import axios from "axios";
  import { onMounted } from "@vue/runtime-core";
  import { useRouter } from "vue-router";
  import { useToast } from "vue-toastification";
  
  export default {
    components: { AdminHeader },
  
    setup() {
      const name           = ref('');
      const phone_number   = ref('');
      const phone_number2  = ref('');
      const phone_number3  = ref('');
      const email          = ref('');
      const email2         = ref('');
      const address        = ref('');
      const city_id        = ref('');
      const state_id       = ref('');
      const country_id     = ref('');
      const logo           = ref('');
      const crudLoading    = ref(false);
      const router         = useRouter();
      const validationErrors = ref({});
      const allErrors      = computed(() => Object.values(validationErrors.value).flat());
      const toast          = useToast();
      const countries      = ref([]);
      const states         = ref([]);
      const cities         = ref([]);
      const country_code         = ref('NG');
  
      const clearError = (field) => { delete validationErrors.value[field]; };

      const filterPhone = (e) => {
        if (!/[\d\s\-+()]/.test(e.key)) e.preventDefault();
      };

      const createCompany = async () => {
        validationErrors.value = {};

        // Client-side: duplicate email check
        if (email.value && email2.value &&
            email.value.trim().toLowerCase() === email2.value.trim().toLowerCase()) {
          validationErrors.value = { email2: ['Email Address 2 cannot be the same as Email Address 1. Please use a different email.'] };
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        // Client-side: duplicate phone check
        const phonePairs = [
          { field: 'phone_number2', label: 'Phone Number 2', val: phone_number2.value?.trim() },
          { field: 'phone_number3', label: 'Phone Number 3', val: phone_number3.value?.trim() },
        ];
        const phone1 = phone_number.value?.trim();
        const phoneErrs = {};
        if (phone1 && phonePairs[0].val && phone1 === phonePairs[0].val)
          phoneErrs.phone_number2 = ['Phone Number 2 is the same as Phone Number 1. Each phone number must be unique.'];
        if (phone1 && phonePairs[1].val && phone1 === phonePairs[1].val)
          phoneErrs.phone_number3 = ['Phone Number 3 is the same as Phone Number 1. Each phone number must be unique.'];
        if (phonePairs[0].val && phonePairs[1].val && phonePairs[0].val === phonePairs[1].val)
          phoneErrs.phone_number3 = ['Phone Number 3 is the same as Phone Number 2. Each phone number must be unique.'];
        if (Object.keys(phoneErrs).length) {
          validationErrors.value = phoneErrs;
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        crudLoading.value = true;
        axios.post('/admin/business/', {
          name: name.value, phone_number: phone_number.value,
          phone_number2: phone_number2.value, phone_number3: phone_number3.value,
          email: email.value, email2: email2.value,
          address: address.value, city_id: city_id.value,
          state_id: state_id.value, country_id: country_id.value,
          logo: logo.value, country_code: country_code.value,
        }).then(res => {
          crudLoading.value = false;
          router.push('/businesses');
          toast.success(res.data.message);
        }).catch(error => {
          crudLoading.value = false;
          if (error.response?.status === 422) {
            const msg = error.response.data.message;
            validationErrors.value = (msg && typeof msg === 'object' && !Array.isArray(msg))
              ? msg
              : { _general: Array.isArray(msg) ? msg : [msg ?? 'Validation failed. Please check your input.'] };
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (error.response?.status === 403) {
            toast.error(error.response.data.message);
          } else {
            const msg = error.response?.data?.message ?? 'An error occurred, please try again';
            validationErrors.value = { _general: Array.isArray(msg) ? msg : [msg] };
            window.scrollTo({ top: 0, behavior: 'smooth' });
            toast.error(Array.isArray(msg) ? msg[0] : msg);
          }
        });
      };
  
      const getAllCountries = async () => {
        const res = await axios.get('countries');
        countries.value = res.data.data.data;
      };
  
      const getStates = async (event) => {
        states.value = []; cities.value = []; state_id.value = ''; city_id.value = '';
        const res = await axios.get('/states/' + event.target.value);
        states.value = res.data.data.data;
      };
  
      const getCities = async (event) => {
        cities.value = []; city_id.value = '';
        const res = await axios.get('/cities/' + event.target.value);
        cities.value = res.data.data.data;
      };
  
      const onFileSelected = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        delete validationErrors.value.logo;
        const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(file.type)) {
          validationErrors.value = { ...validationErrors.value, logo: ['Only PNG or JPG images are allowed. Please select a valid image file.'] };
          event.target.value = '';
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          const sizeMB = (file.size / 1024 / 1024).toFixed(1);
          validationErrors.value = { ...validationErrors.value, logo: [`Image is too large (${sizeMB} MB). Maximum allowed size is 5 MB.`] };
          event.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => { logo.value = e.target.result; };
        reader.readAsDataURL(file);
      };
  
      onMounted(() => { getAllCountries(); });
  
      return {
        crudLoading, validationErrors, allErrors, clearError, filterPhone, name,
        phone_number, phone_number2, phone_number3,
        email, email2, address,
        city_id, state_id, country_id,
        countries, states, cities, logo, country_code,
        createCompany, getAllCountries,
        getStates, getCities, onFileSelected,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header {
    display: flex; align-items: flex-start;
    justify-content: space-between; flex-wrap: wrap;
    gap: 12px; margin-bottom: 20px;
  }
  .page-title { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
  .page-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  /* ── Error Banner ── */
  .error-banner {
    display: flex; align-items: flex-start; gap: 10px;
    background: #fff5f5; border: 1px solid #fac5c5;
    border-radius: 10px; padding: 12px 16px;
    margin-bottom: 20px; color: #e03535; font-size: 13px;
  }
  .error-icon { font-size: 16px; margin-top: 2px; flex-shrink: 0; }
  
  /* ── Form Card ── */
  .form-card {
    background: #fff; border: 1px solid #e4eaf2;
    border-radius: 12px; padding: 28px;
  }
  
  /* ── Section Header ── */
  .section-header { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
  .section-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px; flex-shrink: 0;
  }
  .section-icon--blue   { background: rgba(0,74,173,.1);   color: #004AAD; }
  .section-icon--orange { background: rgba(224,140,0,.1);  color: #e08c00; }
  .section-icon--purple { background: rgba(124,58,237,.1); color: #7c3aed; }
  .section-title { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; }
  .section-sub   { font-size: 12px; color: #7a90b0; margin: 0; }
  
  /* ── Divider ── */
  .section-divider { height: 1px; background: #f0f4f8; margin: 22px 0; }
  
  /* ── Fields ── */
  .fields-grid      { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .fields-col-full  { grid-column: 1 / -1; }
  .field-group      { display: flex; flex-direction: column; gap: 6px; }
  .field-label      { font-size: 13px; font-weight: 600; color: #1a2b45; }
  .req              { color: #e03535; }
  .optional         { font-size: 11px; color: #aab8cc; font-weight: 400; margin-left: 4px; }
  .field-error      { font-size: 11.5px; color: #e03535; }
  
  .field-input {
    padding: 9px 12px; border: 1px solid #dde5f0;
    border-radius: 8px; font-size: 13.5px; color: #1a2b45;
    background: #fff; outline: none; width: 100%;
    transition: border-color .15s, box-shadow .15s;
    font-family: inherit;
  }
  .field-input:focus { border-color: #004AAD; box-shadow: 0 0 0 3px rgba(0,74,173,.08); }
  .field-input::placeholder { color: #aab8cc; }
  .field-input:disabled { background: #f8fafc; color: #aab8cc; cursor: not-allowed; }
  .field-input--error { border-color: #e03535 !important; }
  .field-input--error:focus { box-shadow: 0 0 0 3px rgba(224,53,53,.08) !important; }
  .field-select  { appearance: auto; cursor: pointer; }
  .field-textarea { resize: vertical; min-height: 90px; }
  
  /* ── Logo Upload ── */
  .logo-upload {
    display: inline-block;
    cursor: pointer;
    width: 160px;
  }
  .logo-file-input { display: none; }
  
  .logo-placeholder {
    width: 160px; height: 160px;
    border: 2px dashed #dde5f0;
    border-radius: 12px;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 8px;
    background: #f8fafc;
    transition: border-color .15s, background .15s;
  }
  .logo-upload:hover .logo-placeholder {
    border-color: #004AAD;
    background: #f0f5ff;
  }
  .logo-placeholder-icon {
    width: 44px; height: 44px; border-radius: 10px;
    background: rgba(0,74,173,.1); color: #004AAD;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
  }
  .logo-placeholder-text { font-size: 13px; font-weight: 600; color: #1a2b45; margin: 0; }
  .logo-placeholder-hint { font-size: 11.5px; color: #aab8cc; margin: 0; }
  
  .logo-preview {
    width: 160px; height: 160px;
    border-radius: 12px; overflow: hidden;
    border: 1px solid #e4eaf2;
    position: relative;
  }
  .logo-preview img {
    width: 100%; height: 100%; object-fit: contain;
  }
  .logo-overlay {
    position: absolute; inset: 0;
    background: rgba(0,0,0,0.45);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 6px; color: #fff; font-size: 13px;
    opacity: 0; transition: opacity .15s;
  }
  .logo-upload:hover .logo-overlay { opacity: 1; }
  .logo-upload--filled .logo-preview { border-color: #004AAD; }
  
  /* ── Footer ── */
  .form-footer {
    display: flex; align-items: center; justify-content: flex-end;
    gap: 10px; margin-top: 20px; padding-top: 20px;
    border-top: 1px solid #f0f4f8;
  }
  .btn-primary-action {
    display: inline-flex; align-items: center;
    background: #004AAD; color: #fff;
    font-size: 13px; font-weight: 600;
    padding: 9px 24px; border-radius: 8px; border: none;
    cursor: pointer; transition: background .15s;
  }
  .btn-primary-action:hover:not(:disabled) { background: #003b8e; }
  .btn-primary-action:disabled { opacity: .6; cursor: not-allowed; }
  
  .btn-outline {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff; color: #1a2b45;
    border: 1px solid #dde5f0; font-size: 13px; font-weight: 500;
    padding: 9px 20px; border-radius: 8px;
    cursor: pointer; transition: background .15s;
  }
  .btn-outline:hover { background: #f4f7fb; }
  
  /* ── Responsive ── */
  @media (max-width: 768px) {
    .fields-grid  { grid-template-columns: 1fr; }
    .form-card    { padding: 18px; }
    .form-footer  { flex-direction: column; }
    .btn-primary-action, .btn-outline { width: 100%; justify-content: center; }
  }
  </style>